import { useState, useEffect, useRef } from "react";
import { Chat, ChatStatus } from "../../../entity/Chat";
import { VMessage } from "../../../entity/VMessage";
import "./SupportByChatManagerScreen.css";
import { useTranslation } from "react-i18next";
import { IoCheckmarkDoneOutline, IoSend } from "react-icons/io5";
import { BiSupport } from "react-icons/bi";
import { useSelector } from "react-redux";
import { selectStore } from "../../../store/selectors/StoreSelector";
import WSChat from "../../../services/remote/chat/WSChat";
import { selectUser } from "../../../store/selectors/UserSelector";
import { FaImage, FaRegImages } from "react-icons/fa";
import { WSMedia } from "../../../services/remote/media/WSMedia";
import { Badge } from "../../components/Badge";
import { COLORS } from "../../../utils/Colors";

const formatTime = (date: Date) =>
  new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);

const formatRelativeDay = (date: Date) => {
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return formatTime(date);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(date);
};

const getInitials = (title: string) =>
  title
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

export const SupportByChatManagerScreen = () => {
  const { t } = useTranslation();
  const [selectedChatId, setSelectedChatId] = useState<string | null>();
  const [chats, setChats] = useState<Chat[] | undefined>();
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<VMessage[]>();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [chatTitle, setChatTitle] = useState("");

  const mediaInstance = WSMedia.getInstance();

  const user = useSelector(selectUser);
  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setSelectedImage(file);

    const reader = new FileReader();

    reader.onload = () => {
      setPreview(reader.result as string);
    };

    reader.readAsDataURL(file);
  };

  const selectedChat = chats?.find((c) => c.id === selectedChatId) ?? null;
  const conversation = messages
    ?.filter((m) => m.chatId === selectedChatId)
    .sort(
      (a, b) => (a.createdAt?.getTime() || 0) - (b.createdAt?.getTime() || 0),
    );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation?.length, selectedChatId]);

  useEffect(() => {
    const fetchUrl = async () => {
      try {
        const url = await mediaInstance.getGeneratedURL();
        setGeneratedUrl(url);
      } catch (error) {
        console.error("Failed to get generated URL:", error);
      }
    };

    fetchUrl();
  }, []);

  const handleSendMessage = async () => {
    if (!selectedChatId) return;

    const trimmed = draft.trim();

    if (!trimmed && !selectedImage) return;

    let imageUrl: string | undefined;

    if (selectedImage) {
      imageUrl = await mediaInstance.saveImage(generatedUrl, selectedImage);
    }

    const newMessage: VMessage = {
      chatId: selectedChatId,
      senderId: user!.id!,
      storeId: selectedChat?.storeId!,

      content: trimmed,
      imageUrl,

      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setMessages((prev) => [...(prev || []), newMessage]);

    setDraft("");
    setSelectedImage(null);
    setPreview(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    WSChat.sendMessage(newMessage);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    WSChat.getAllChats().then((chats) => {
      setChats(chats);
    });
  }, []);

  useEffect(() => {
    if (!selectedChatId) return;
    if (selectedChat?.status === ChatStatus.PENDING) {
      WSChat.updateChatStatus(selectedChatId!, ChatStatus.OPEN);
    }

    const unsubscribe = WSChat.getMessageByChatIdRealtime(
      selectedChatId,
      (messages) => {
        setMessages(messages);
      },
      (error) => {
        console.error(error);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedChatId]);

  const handleCompleteChat = () => {
    if (!selectedChatId) return;

    WSChat.updateChatStatus(selectedChatId, ChatStatus.CLOSED).then(() => {
      setSelectedChatId(null);
    });
  };

  const handleUpdateChatTitle = () => {
    if (!selectedChatId) return;

    WSChat.updateChatTitle(selectedChatId, chatTitle);

    setChats((prev) =>
      prev?.map((chat) =>
        chat.id === selectedChatId ? { ...chat, title: chatTitle } : chat,
      ),
    );
  };

  return (
    <div className="support-by-chat-container">
      <aside className="chat-list">
        <div className="chat-list-header">{t("chat.title")}</div>
        <div className="chat-list-items">
          {chats?.map((chat) => {
            const isActive = chat.id === selectedChatId;
            const lastMsg = messages
              ?.filter((m) => m.chatId === chat.id)
              .sort(
                (a, b) =>
                  (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0),
              )[0];

            return (
              <div
                key={chat.id}
                className={`chat-list-item ${isActive ? "active" : ""}`}
                onClick={() => setSelectedChatId(chat.id!)}
              >
                <div className="chat-item-body">
                  <div className="chat-item-top">
                    <span className="chat-item-title">
                      {chat.title || t("chat.no_title")}
                    </span>
                    <span className="chat-item-time">
                      {formatRelativeDay(chat.updatedAt || new Date())}
                    </span>
                  </div>
                  <p className="chat-item-preview">
                    <span>{chat.createdAt?.toLocaleString()}</span>
                    <span
                      className="chat-item-status"
                      style={{
                        background:
                          chat.status === ChatStatus.OPEN
                            ? COLORS.Palette.DarkYellow
                            : chat.status === ChatStatus.PENDING
                              ? COLORS.Brand.Primary
                              : COLORS.Palette.Teal,
                      }}
                    >
                      {t("chat." + (chat.status || "pending"))}
                    </span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      <div className="conversation-container">
        {selectedChat ? (
          <>
            <div className="conversation-header">
              <div className="conversation-header-text">
                <span className="conversation-title">{selectedChat.title}</span>

                <span
                  className="chat-item-status"
                  style={{
                    background:
                      selectedChat.status === ChatStatus.OPEN
                        ? COLORS.Palette.DarkYellow
                        : selectedChat.status === ChatStatus.PENDING
                          ? COLORS.Brand.Primary
                          : COLORS.Palette.Teal,
                  }}
                >
                  {t("chat." + (selectedChat.status || "pending"))}
                </span>
              </div>

              {/** Set a title for the chat */}
              <div className="chat-actions">
                <input
                  type="text"
                  className="chat-name"
                  value={selectedChat.title}
                  onChange={(e) => setChatTitle(e.target.value)}
                  placeholder={t("chat.title")}
                />
                <button
                  className="primary-button-sm"
                  onClick={handleUpdateChatTitle}
                >
                  {t("chat.update")}
                </button>

                <button
                  className="primary-button-sm"
                  onClick={handleCompleteChat}
                >
                  {t("chat.completed")}{" "}
                  <IoCheckmarkDoneOutline
                    color={COLORS.Neutral.Gray}
                    size={20}
                    style={{ paddingLeft: 4 }}
                  />
                </button>
              </div>
            </div>

            <div className="conversation-messages">
              {conversation?.length === 0 ? (
                <div className="conversation-empty">
                  <BiSupport size={32} />
                  <p>{t("chat.noMessages")}</p>
                </div>
              ) : (
                conversation?.map((msg) => {
                  const isOwn = msg.senderId === user?.id;
                  return (
                    <div
                      key={msg.id}
                      className={`message-row ${isOwn ? "own" : "other"}`}
                    >
                      {msg.imageUrl ? (
                        <img
                          src={msg.imageUrl}
                          alt="Message"
                          className="message-image"
                        />
                      ) : (
                        <div className="message-bubble">
                          {msg.content && <p>{msg.content}</p>}

                          <span className="message-time">
                            {formatTime(msg.createdAt || new Date())}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            <>
              {preview && (
                <div className="image-preview-container">
                  <div className="image-preview">
                    <button
                      type="button"
                      className="remove-image-button"
                      onClick={() => {
                        setPreview(null);
                        setSelectedImage(null);

                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                    >
                      ✕
                    </button>
                    <img src={preview} alt="Preview" width="10%" />
                  </div>
                </div>
              )}

              <div className="conversation-input">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                />

                <button
                  type="button"
                  className="image-button"
                  onClick={() => fileInputRef.current?.click()}
                  title="Add image"
                >
                  <FaRegImages color={COLORS.Neutral.Gray} size={26} />
                </button>

                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t("chat.typeMessage") as string}
                  rows={1}
                />

                <button
                  className="send-button"
                  onClick={handleSendMessage}
                  disabled={!draft.trim() && !selectedImage}
                  aria-label={t("chat.send") as string}
                >
                  <IoSend size={18} />
                </button>
              </div>
            </>
          </>
        ) : (
          <div className="conversation-empty full">
            <BiSupport size={40} />
            <p>{t("chat.selectConversation")}</p>
          </div>
        )}
      </div>
    </div>
  );
};
